import { useCallback, useEffect, useReducer, useRef, useState } from 'react';

import {
  getSiteCmsDomainApi,
  getSiteCmsDraftApi,
  getSiteCmsPublicationStateApi,
  listSiteCmsDocumentsApi,
  saveSiteCmsDraftApi,
} from '@/lib/api/siteCmsApi';
import { useAuthStore } from '@/stores/auth-store';
import { defaultCmsBranding } from '@shared/cms/content';
import type { SiteCmsDraftResponse, SiteCmsOwnerType } from '@shared/types/entities';
import { useIsMutating, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  ArrowRight,
  Check,
  ChevronRight,
  Eye,
  Files,
  Globe2,
  LayoutDashboard,
  Loader2,
  Menu,
  Monitor,
  Network,
  Palette,
  RefreshCw,
  Rocket,
  Save,
  Settings2,
  ShieldCheck,
  Smartphone,
} from 'lucide-react';
import { toast } from 'sonner';

import SiteCmsDesign from './SiteCmsDesign';
import { SiteCmsDialog, SiteCmsInteractions } from './SiteCmsDialog';
import SiteCmsDocuments from './SiteCmsDocuments';
import SiteCmsImages from './SiteCmsImages';
import SiteCmsNavigationEditor from './SiteCmsNavigationEditor';
import SiteCmsNavigationGuard from './SiteCmsNavigationGuard';
import SiteCmsPageManager from './SiteCmsPageManager';
import SiteCmsPreview from './SiteCmsPreview';
import SiteCmsPublicationPanel from './SiteCmsPublicationPanel';
import { cmsCanLeaveAfterSave } from './site-cms-interaction-model';
import {
  type CmsSection,
  type CmsSession,
  cmsPublicationLabel,
  cmsSessionReducer,
  isCmsDirty,
} from './site-cms-session';
import { type CmsIssue, cmsDraftIssues } from './site-cms-workspace-model';
import './site-cms-workspace.css';
import { useCmsInteractions } from './use-cms-interactions';

type EditorProps = {
  ownerType: SiteCmsOwnerType;
  ownerId: number;
  ownerName: string;
  onChangeOwner?: () => void;
  accessSuspended?: boolean;
  onRetryAccess?: () => void;
};
const sections = [
  {
    id: 'overview',
    label: 'Vue d’ensemble',
    icon: LayoutDashboard,
    title: 'Votre site',
    subtitle: 'Les bonnes actions, au bon endroit.',
  },
  {
    id: 'pages',
    label: 'Pages',
    icon: Files,
    title: 'Pages',
    subtitle: 'Retrouvez vos pages dans le tableau, puis ouvrez uniquement celle à modifier.',
  },
  {
    id: 'design',
    label: 'Apparence',
    icon: Palette,
    title: 'Votre identité visuelle',
    subtitle: 'Images, ambiance et personnalisation, avec un aperçu fidèle.',
  },
  {
    id: 'navigation',
    label: 'Navigation',
    icon: Network,
    title: 'Organiser le menu',
    subtitle: 'Des liens lisibles, jusqu’à trois niveaux.',
  },
  {
    id: 'documents',
    label: 'Images & documents',
    icon: Files,
    title: 'Votre bibliothèque',
    subtitle: 'Des fichiers privés, avec des usages et une diffusion explicites.',
  },
  {
    id: 'publication',
    label: 'Publication',
    icon: Rocket,
    title: 'Vérifier et publier',
    subtitle: 'Distinguez votre travail de ce que voient les visiteurs.',
  },
  {
    id: 'settings',
    label: 'Réglages',
    icon: Settings2,
    title: 'Les informations du site',
    subtitle: 'Identité, coordonnées et pied de page.',
  },
] as const;
// Memory only, scoped to authenticated user AND tenant. Nothing is written to localStorage.
const recovery = new Map<string, { session: CmsSession; expires: number }>();
export default function SiteCmsEditor(props: EditorProps) {
  const userId = useAuthStore(state => state.user?.id);
  return (
    <SiteCmsInteractions key={`${userId}:${props.ownerType}:${props.ownerId}`}>
      <SiteCmsLoader {...props} recoveryKey={`${userId}:${props.ownerType}:${props.ownerId}`} />
    </SiteCmsInteractions>
  );
}
function SiteCmsLoader(props: EditorProps & { recoveryKey: string }) {
  const query = useQuery({
    queryKey: ['site-cms', 'draft', props.ownerType, props.ownerId],
    queryFn: () => getSiteCmsDraftApi(props.ownerType, props.ownerId),
    retry: false,
    staleTime: Infinity,
    refetchOnWindowFocus: false,
  });
  if (!query.data && query.isError)
    return (
      <div className='cms-alert' role='alert'>
        Brouillon inaccessible.{' '}
        <button type='button' onClick={() => void query.refetch()}>
          Réessayer
        </button>
      </div>
    );
  if (!query.data)
    return (
      <div className='cms-loading'>
        <Loader2 className='animate-spin' size={22} />
        Chargement de votre site…
      </div>
    );
  if (query.data.ownerId !== props.ownerId || query.data.ownerType !== props.ownerType)
    return (
      <div className='cms-alert' role='alert'>
        Le brouillon ne correspond pas à la structure sélectionnée.
      </div>
    );
  return (
    <SiteCmsWorkspace
      {...props}
      initialDraft={query.data}
      reload={async () => {
        const response = await query.refetch();
        if (response.isError || !response.data) throw new Error('Le rechargement a échoué. Votre copie est conservée.');
        return response.data;
      }}
    />
  );
}
function SiteCmsWorkspace({
  ownerType,
  ownerId,
  ownerName,
  initialDraft,
  reload,
  onChangeOwner,
  accessSuspended,
  onRetryAccess,
  recoveryKey,
}: EditorProps & {
  initialDraft: SiteCmsDraftResponse;
  reload: () => Promise<SiteCmsDraftResponse>;
  recoveryKey: string;
}) {
  const [session, dispatch] = useReducer(cmsSessionReducer, initialDraft, initial => {
    const previous = recovery.get(recoveryKey);
    return previous && previous.expires > Date.now() ? previous.session : { draft: initial, saved: initial };
  });
  const [recoveredNotice, setRecoveredNotice] = useState(() => {
    const previous = recovery.get(recoveryKey);
    if (!previous || previous.expires <= Date.now()) return '';
    return previous.session.saved.revision !== initialDraft.revision
      ? 'Votre copie locale non enregistrée est restaurée. Une autre révision existe sur le serveur : comparez-la avant de remplacer son contenu.'
      : 'Votre copie locale non enregistrée est restaurée. Vérifiez-la puis enregistrez le brouillon.';
  });
  const { ask, pendingCount, pendingLabels, busyCount: libraryBusy } = useCmsInteractions();
  const [section, setSection] = useState<CmsSection>('overview');
  const [visited, setVisited] = useState<CmsSection[]>(['overview']);
  const editSequence = useRef(0);
  const saving = useRef(false);
  const latestActivity = useRef({ pendingCount, libraryBusy });
  useEffect(() => {
    latestActivity.current = { pendingCount, libraryBusy };
  }, [pendingCount, libraryBusy]);
  const [notice, setNotice] = useState('');
  const [selected, setSelected] = useState('home');
  const [pageOpenRequest, setPageOpenRequest] = useState(0);
  const [focusIssue, setFocusIssue] = useState<{ pageSlug?: string; blockIndex?: number; serial: number }>({
    serial: 0,
  });
  const [navOpen, setNavOpen] = useState(false);
  const [preview, setPreview] = useState(false);
  const [previewMobile, setPreviewMobile] = useState(false);
  const [previewPageSlug, setPreviewPageSlug] = useState('home');
  const [mediaTab, setMediaTab] = useState<'images' | 'documents'>('images');
  const [mediaRevision, setMediaRevision] = useState(0);
  const [savedMediaRevision, setSavedMediaRevision] = useState(0);
  const [contentEpoch, setContentEpoch] = useState(0);
  const [reloading, setReloading] = useState(false);
  const [error, setError] = useState<{ message: string; conflict: boolean; field?: string } | null>(null);
  const [showIssues, setShowIssues] = useState(false);
  const [showChanges, setShowChanges] = useState(false);
  const queryClient = useQueryClient();
  const heading = useRef<HTMLHeadingElement>(null);
  const current = session.draft;
  const dirty = isCmsDirty(session) || mediaRevision !== savedMediaRevision;
  const operationCount = useIsMutating({ mutationKey: ['site-cms', 'operation', ownerType, ownerId] });
  const draftKey = ['site-cms', 'draft', ownerType, ownerId];
  const documents = useQuery({
    queryKey: ['site-cms', 'documents', ownerType, ownerId],
    queryFn: () => listSiteCmsDocumentsApi(ownerType, ownerId),
    retry: false,
  });
  const domain = useQuery({
    queryKey: ['site-cms', 'domain', ownerType, ownerId],
    queryFn: () => getSiteCmsDomainApi(ownerType, ownerId),
    retry: false,
  });
  const publication = useQuery({
    queryKey: ['site-cms', 'publication-state', ownerType, ownerId],
    queryFn: () => getSiteCmsPublicationStateApi(ownerType, ownerId),
    retry: false,
  });
  const issues = cmsDraftIssues(current);
  const save = useMutation({
    mutationFn: ({ draft: submitted }: { draft: SiteCmsDraftResponse; mediaRevision: number }) =>
      saveSiteCmsDraftApi({
        ownerType,
        ownerId,
        expectedRevision: submitted.revision,
        title: submitted.title,
        description: submitted.description,
        theme: submitted.theme,
        branding: submitted.branding,
        pages: submitted.pages,
        headerMenu: submitted.headerMenu,
      }),
    onSuccess: (response, { draft: submitted, mediaRevision: revision }) => {
      setSavedMediaRevision(revision);
      dispatch({ type: 'saved', response, submitted });
      queryClient.setQueryData(draftKey, response);
      void queryClient.invalidateQueries({ queryKey: ['site-cms', 'publication-state', ownerType, ownerId] });
      setError(null);
      toast.success('Brouillon enregistré. Le site public n’a pas changé.');
    },
    onError: reason => {
      const details = reason as Error & { status?: number; details?: { field?: string } };
      setError({
        message: reason.message || 'Enregistrement impossible.',
        conflict: details.status === 409,
        field: details.details?.field,
      });
    },
  });
  // External checkpoint, not state mirrored from a request. Protects auth/route remounts.
  useEffect(() => {
    if (dirty) recovery.set(recoveryKey, { session, expires: Date.now() + 15 * 60_000 });
    else recovery.delete(recoveryKey);
  }, [session, dirty, recoveryKey]);
  useEffect(() => {
    const discard = () => recovery.delete(recoveryKey);
    const logout = () => recovery.clear();
    window.addEventListener('cms:discard-local-copy', discard);
    window.addEventListener('auth:logout', logout);
    return () => {
      window.removeEventListener('cms:discard-local-copy', discard);
      window.removeEventListener('auth:logout', logout);
    };
  }, [recoveryKey]);
  const submit = useCallback(async (): Promise<boolean> => {
    if (saving.current || save.isPending || reloading || operationCount || libraryBusy || accessSuspended) return false;
    if (pendingCount) {
      setNotice(`Terminez ou annulez les formulaires avant l’enregistrement : ${pendingLabels.join(', ')}.`);
      return false;
    }
    if (
      !dirty &&
      current.revision > 0 &&
      !publication.data?.requiresResave &&
      !(publication.data?.latest?.status === 'REVOKED' && publication.data.latest.revision === current.revision)
    )
      return true;
    const invalid = cmsDraftIssues(current).filter(i => !i.publicationOnly);
    if (invalid.length) {
      setShowIssues(true);
      setError({
        message:
          'Complétez les champs signalés. Votre travail reste dans l’éditeur ; il n’a pas encore été enregistré.',
        conflict: false,
      });
      return false;
    }
    const sequence = editSequence.current;
    saving.current = true;
    try {
      await save.mutateAsync({ draft: current, mediaRevision });
      const complete = cmsCanLeaveAfterSave(
        sequence,
        editSequence.current,
        latestActivity.current.pendingCount,
        latestActivity.current.libraryBusy > 0,
      );
      setNotice(
        complete
          ? ''
          : 'La version envoyée est enregistrée. Vos modifications plus récentes restent ici ; enregistrez-les avant de quitter.',
      );
      return complete;
    } catch {
      return false;
    } finally {
      saving.current = false;
    }
  }, [
    save,
    current,
    mediaRevision,
    reloading,
    operationCount,
    accessSuspended,
    pendingCount,
    pendingLabels,
    libraryBusy,
    dirty,
    publication.data,
  ]);
  useEffect(() => {
    const shortcut = (event: KeyboardEvent) => {
      if (!(event.ctrlKey || event.metaKey) || event.altKey || event.key.toLowerCase() !== 's') return;
      event.preventDefault();
      if (document.querySelector('dialog[open]')) return;
      void submit();
    };
    window.addEventListener('keydown', shortcut);
    return () => window.removeEventListener('keydown', shortcut);
  }, [submit]);
  function navigate(next: CmsSection) {
    setSection(next);
    setVisited(old => (old.includes(next) ? old : [...old, next]));
    setNavOpen(false);
    requestAnimationFrame(() => heading.current?.focus());
  }
  function edit(draft: SiteCmsDraftResponse) {
    editSequence.current += 1;
    dispatch({ type: 'edit', draft });
  }
  function openIssue(issue: CmsIssue) {
    if (issue.pageSlug) {
      setSelected(issue.pageSlug);
      setFocusIssue(old => ({ pageSlug: issue.pageSlug, blockIndex: issue.blockIndex, serial: old.serial + 1 }));
      navigate('pages');
    } else navigate(issue.section ?? 'settings');
  }
  async function reloadDraft() {
    if (
      (dirty || pendingCount) &&
      (await ask({
        title: 'Recharger le brouillon enregistré ?',
        message:
          'Les modifications du site non enregistrées seront remplacées. Les imports déjà envoyés restent dans la bibliothèque.',
        choices: [{ value: 'reload', label: 'Abandonner le brouillon local', danger: true }],
      })) !== 'reload'
    )
      return;
    setReloading(true);
    try {
      const next = await reload();
      dispatch({ type: 'reload', draft: next });
      setContentEpoch(value => value + 1);
      setSelected('home');
      setSavedMediaRevision(mediaRevision);
      setError(null);
      setShowIssues(false);
    } catch (reason) {
      setError({ message: reason instanceof Error ? reason.message : 'Rechargement impossible.', conflict: false });
    } finally {
      setReloading(false);
    }
  }
  async function rebase() {
    if (
      (await ask({
        title: 'Conserver votre copie comme nouvelle version ?',
        message:
          'Votre contenu local remplacera le brouillon serveur uniquement lors du prochain enregistrement. Cette action ne fusionne pas les modifications de l’autre session et ne publie rien.',
        choices: [{ value: 'rebase', label: 'Reprendre avec ma copie locale' }],
      })) !== 'rebase'
    )
      return;
    setReloading(true);
    try {
      const latest = await reload();
      dispatch({ type: 'rebase', server: latest });
      setError(null);
    } catch (reason) {
      setError({ message: reason instanceof Error ? reason.message : 'Comparaison impossible.', conflict: true });
    } finally {
      setReloading(false);
    }
  }
  const active = sections.find(item => item.id === section) ?? sections[0];
  const status = publication.isError
    ? 'Statut public indisponible'
    : publication.isPending
      ? 'Statut public en cours de vérification…'
      : publication.data?.live
        ? cmsPublicationLabel(publication.data.live)
        : 'Site hors ligne';
  const needsRevision =
    publication.data?.requiresResave ||
    (publication.data?.latest?.status === 'REVOKED' && publication.data.latest.revision === current.revision);
  const savedTime = session.saved.updatedAt
    ? new Date(session.saved.updatedAt).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })
    : null;
  const saveLabel = save.isPending
    ? 'Enregistrement…'
    : dirty
      ? 'Modifications non enregistrées'
      : current.revision === 0
        ? 'Brouillon initial à enregistrer'
        : `Brouillon enregistré${savedTime ? ` à ${savedTime}` : ''}`;
  const metaChanged = () => {
    editSequence.current += 1;
    setMediaRevision(value => value + 1);
    void queryClient.invalidateQueries({ queryKey: ['site-cms', 'publication-state', ownerType, ownerId] });
  };
  const changedPages = current.pages
    .filter(p => JSON.stringify(p) !== JSON.stringify(session.saved.pages.find(old => old.slug === p.slug)))
    .map(p => p.title);
  return (
    <div className='cms-shell' data-testid='cms-workspace'>
      {recoveredNotice && (
        <div className='cms-notice' role='status'>
          <p>{recoveredNotice}</p>
          <button type='button' className='cms-link-button' onClick={() => setRecoveredNotice('')}>
            J’ai compris
          </button>
        </div>
      )}
      <SiteCmsNavigationGuard
        dirty={dirty}
        pendingForms={pendingCount > 0}
        busy={operationCount > 0 || libraryBusy > 0 || save.isPending || reloading}
        save={submit}
      />
      <header className='cms-toolbar'>
        <div className='cms-toolbar-title'>
          <div className='cms-brand-icon'>
            <Globe2 size={23} />
          </div>
          <div>
            <span className='cms-breadcrumb'>
              Mon site <ChevronRight size={13} />
              {active.label}
            </span>
            <h1>{ownerName}</h1>
          </div>
        </div>
        <div className='cms-toolbar-actions'>
          <span className={`cms-save-state ${dirty ? 'is-dirty' : ''}`} role='status'>
            {saveLabel}
          </span>
          <button
            type='button'
            className='cms-button'
            disabled={accessSuspended}
            onClick={() => {
              setPreviewPageSlug(selected);
              setPreview(true);
            }}
          >
            <Eye size={17} />
            Aperçu
          </button>
          <button
            type='button'
            className={`cms-button ${!dirty && current.revision > 0 ? 'cms-button-primary' : ''}`}
            disabled={accessSuspended}
            onClick={() => navigate('publication')}
          >
            <Rocket size={17} />
            Vérifier et publier
          </button>
          <button
            type='button'
            className={`cms-button ${dirty || !current.revision ? 'cms-button-primary' : ''}`}
            aria-keyshortcuts='Control+s Meta+s'
            disabled={
              save.isPending ||
              reloading ||
              operationCount > 0 ||
              libraryBusy > 0 ||
              accessSuspended ||
              (!dirty && !needsRevision && current.revision > 0)
            }
            onClick={() => void submit()}
          >
            {save.isPending ? <Loader2 size={17} className='animate-spin' /> : <Save size={17} />}
            {needsRevision && !dirty ? 'Actualiser le brouillon' : 'Enregistrer le brouillon'}
          </button>
        </div>
      </header>
      <div className='cms-status-strip'>
        <span>{status}</span>
        <button type='button' className='cms-link-button' onClick={() => setShowChanges(v => !v)}>
          Voir les changements locaux
        </button>
        {pendingCount > 0 && <span>À terminer : {pendingLabels.join(' · ')}</span>}
        {libraryBusy > 0 && <strong role='status'>Opération de bibliothèque en cours…</strong>}
        {operationCount > 0 && <strong role='status'>Publication en cours — version demandée en traitement</strong>}
      </div>
      {notice && (
        <p className='cms-notice' role='status'>
          {notice}
          <button type='button' className='cms-link-button' onClick={() => setNotice('')}>
            Masquer
          </button>
        </p>
      )}
      {accessSuspended && (
        <div className='cms-alert' role='alert'>
          <strong>Vérification de vos droits indisponible.</strong>
          <p>
            Votre copie est conservée. Les sauvegardes et publications restent suspendues tant que les droits n’ont pas
            été revalidés.
          </p>
          {onRetryAccess && (
            <button type='button' className='cms-button' onClick={onRetryAccess}>
              Réessayer la vérification des droits
            </button>
          )}
        </div>
      )}
      {showChanges && (
        <section className='cms-notice'>
          <strong>Depuis le dernier enregistrement</strong>
          <ul>
            {changedPages.map((name, i) => (
              <li key={i}>Page : {name}</li>
            ))}
            {current.pages.length < session.saved.pages.length && <li>Page(s) retirée(s)</li>}
            {JSON.stringify(current.headerMenu) !== JSON.stringify(session.saved.headerMenu) && (
              <li>Navigation modifiée</li>
            )}
            {JSON.stringify([current.theme, current.branding]) !==
              JSON.stringify([session.saved.theme, session.saved.branding]) && <li>Apparence modifiée</li>}
            {(current.title !== session.saved.title || current.description !== session.saved.description) && (
              <li>Informations du site modifiées</li>
            )}
            {mediaRevision !== savedMediaRevision && <li>Métadonnées de bibliothèque modifiées</li>}
          </ul>
          <p>
            Cette comparaison concerne le brouillon enregistré, pas une comparaison complète avec la version publique.
          </p>
        </section>
      )}
      {error && (
        <section className='cms-alert' role='alert'>
          <strong>
            {error.conflict ? 'Une autre session a enregistré ce site.' : 'Le brouillon n’a pas été enregistré.'}
          </strong>
          <p>{error.message} Votre copie locale est conservée.</p>
          {error.field && (
            <button
              type='button'
              className='cms-button'
              onClick={() => {
                const match = /pages\.(\d+)(?:\.blocks\.(\d+))?/.exec(error.field ?? '');
                if (match && current.pages[Number(match[1])])
                  openIssue({
                    id: 'server',
                    message: error.message,
                    pageSlug: current.pages[Number(match[1])].slug,
                    blockIndex: match[2] ? Number(match[2]) : undefined,
                  });
              }}
            >
              Ouvrir le contenu concerné
            </button>
          )}
          {error.conflict && (
            <div className='cms-row'>
              <button type='button' className='cms-button' onClick={() => void reloadDraft()}>
                Charger la version serveur
              </button>
              <button type='button' className='cms-button' onClick={() => void rebase()}>
                Garder ma copie locale
              </button>
            </div>
          )}
        </section>
      )}
      {(showIssues || issues.length > 0) && issues.length > 0 && (
        <details className='cms-issues' open={showIssues}>
          <summary>{issues.length} point(s) à vérifier</summary>
          <ul>
            {issues.map(issue => (
              <li key={issue.id}>
                <button type='button' onClick={() => openIssue(issue)}>
                  {issue.pageSlug ? `${current.pages.find(p => p.slug === issue.pageSlug)?.title} · ` : ''}
                  {issue.message}
                  {issue.publicationOnly ? ' (publication)' : ''}
                </button>
              </li>
            ))}
          </ul>
        </details>
      )}
      <div className='cms-workspace-grid'>
        <aside className={`cms-sidebar ${navOpen ? 'is-open' : ''}`} aria-label='Espace de gestion du site'>
          <div className='cms-sidebar-heading'>
            <span className='cms-kicker'>Menu du site</span>
            <span className='cms-pill cms-pill-green'>
              <ShieldCheck size={13} />
              Activé
            </span>
            <button
              type='button'
              className='cms-nav-toggle cms-icon-button'
              aria-label='Afficher la navigation CMS'
              aria-expanded={navOpen}
              onClick={() => setNavOpen(v => !v)}
            >
              <Menu size={21} />
            </button>
          </div>
          <nav aria-label='Navigation CMS'>
            {sections.map(item => {
              const Icon = item.icon;
              return (
                <button
                  key={item.id}
                  type='button'
                  className={`cms-nav-item ${section === item.id ? 'is-active' : ''}`}
                  aria-current={section === item.id ? 'page' : undefined}
                  onClick={() => navigate(item.id)}
                >
                  <Icon size={18} />
                  <span>{item.label}</span>
                  {section === item.id && <ChevronRight size={15} />}
                </button>
              );
            })}
          </nav>
          <div className='cms-sidebar-footer'>
            <strong>Un espace, votre site</strong>
            <p>Enregistrer conserve votre travail. Publier le rend visible.</p>
            {onChangeOwner && (
              <button type='button' className='cms-link-button' onClick={onChangeOwner}>
                Changer d’organisation
              </button>
            )}
          </div>
        </aside>
        <section className='cms-main-panel' aria-label='Gestion du site'>
          <div className='cms-section-heading'>
            <div>
              <span className='cms-kicker'>{active.label}</span>
              <h2 tabIndex={-1} ref={heading}>
                {active.title}
              </h2>
              <p>{active.subtitle}</p>
            </div>
          </div>
          <fieldset key={contentEpoch} className='cms-workspace-content' disabled={reloading || accessSuspended}>
            {section === 'overview' && (
              <div className='cms-overview-r2'>
                <section className='cms-card cms-overview-status'>
                  <span className='cms-kicker'>Votre adresse</span>
                  <h3>
                    {domain.isPending
                      ? 'Lecture du domaine…'
                      : domain.isError
                        ? 'Domaine indisponible'
                        : (domain.data?.hostname ?? 'Domaine à configurer')}
                  </h3>
                  <div className='cms-row'>
                    <span className='cms-pill'>{status}</span>
                    <span>{saveLabel}</span>
                  </div>
                  <p>
                    {dirty
                      ? 'Commencez par enregistrer vos changements. Les visiteurs voient encore la version précédente.'
                      : (publication.data?.reason ??
                        'Votre brouillon peut être vérifié avant sa prochaine publication.')}
                  </p>
                  <div className='cms-row'>
                    <button
                      type='button'
                      className='cms-button cms-button-primary'
                      onClick={() => {
                        setSelected('home');
                        setPageOpenRequest(value => value + 1);
                        navigate('pages');
                      }}
                    >
                      Modifier l’accueil <ArrowRight size={17} />
                    </button>
                    <button
                      type='button'
                      className='cms-button'
                      onClick={() => {
                        setPreviewPageSlug(selected);
                        setPreview(true);
                      }}
                    >
                      <Eye size={17} />
                      Voir l’aperçu
                    </button>
                  </div>
                </section>
                <div className='cms-stats'>
                  <div className='cms-card'>
                    <strong>{current.pages.length}</strong>
                    <span>pages dans votre site</span>
                  </div>
                  <div className='cms-card'>
                    <strong>{documents.isPending || documents.isError ? '—' : (documents.data?.length ?? 0)}</strong>
                    <span>documents privés</span>
                  </div>
                  <div className='cms-card'>
                    <strong>{{ marine: 'Marine', sable: 'Sable', ardoise: 'Ardoise' }[current.theme]}</strong>
                    <span>ambiance</span>
                  </div>
                </div>
                <section className='cms-card'>
                  <h3>Vos tâches courantes</h3>
                  <div className='cms-quick-actions'>
                    {[
                      {
                        title: 'Créer ou modifier une page',
                        caption: 'Contenu, sections et diffusion',
                        next: 'pages' as const,
                        icon: Files,
                      },
                      {
                        title: 'Changer votre apparence',
                        caption: 'Logo, photos et couleurs',
                        next: 'design' as const,
                        icon: Palette,
                      },
                      {
                        title: 'Ajouter un document',
                        caption: 'Règlements et informations utiles',
                        next: 'documents' as const,
                        icon: Files,
                      },
                    ].map(item => {
                      const Icon = item.icon;
                      return (
                        <button
                          type='button'
                          key={item.next}
                          onClick={() => {
                            if (item.next === 'documents') setMediaTab('documents');
                            navigate(item.next);
                          }}
                        >
                          <Icon size={20} />
                          <span>
                            <b>{item.title}</b>
                            <small>{item.caption}</small>
                          </span>
                          <ArrowRight size={17} />
                        </button>
                      );
                    })}
                  </div>
                </section>
                {!publication.data?.live && (
                  <section className='cms-card'>
                    <h3>Première mise en ligne</h3>
                    <div className='cms-checklist'>
                      {[
                        { done: Boolean(current.title.trim()), label: 'Un nom pour votre site' },
                        { done: current.pages.some(p => p.slug === 'home'), label: 'Une page d’accueil' },
                        {
                          done: Boolean(domain.data?.verifiedAt),
                          label: domain.isError
                            ? 'Domaine : vérification indisponible'
                            : 'Domaine validé par BoulesConnect',
                        },
                      ].map(item => (
                        <div key={item.label}>
                          <span className={item.done ? 'is-done' : ''}>{item.done ? <Check size={14} /> : '○'}</span>
                          {item.label}
                        </div>
                      ))}
                    </div>
                    <p className='cms-hint'>Aucun logo, partenaire ou PDF n’est obligatoire.</p>
                  </section>
                )}
              </div>
            )}
            {visited.includes('pages') && (
              <div hidden={section !== 'pages'}>
                <SiteCmsPageManager
                  draft={current}
                  onChange={edit}
                  selected={selected}
                  onSelect={setSelected}
                  onPreview={slug => {
                    setSelected(slug);
                    setPreviewPageSlug(slug);
                    setPreview(true);
                  }}
                  focusRequest={focusIssue}
                  openRequest={pageOpenRequest}
                />
              </div>
            )}
            {visited.includes('design') && (
              <div hidden={section !== 'design'}>
                <SiteCmsDesign active={section === 'design'} draft={current} onChange={edit} />
              </div>
            )}
            {visited.includes('navigation') && (
              <div hidden={section !== 'navigation'}>
                <section className='cms-card'>
                  <h3>Menu principal</h3>
                  <SiteCmsNavigationEditor
                    items={current.headerMenu}
                    revision={current.revision}
                    pages={current.pages}
                    documents={documents.data ?? []}
                    onChange={headerMenu => edit({ ...current, headerMenu })}
                  />
                </section>
              </div>
            )}
            {visited.includes('documents') && (
              <div hidden={section !== 'documents'} className='cms-stack'>
                <div className='cms-segmented' role='group' aria-label='Bibliothèque'>
                  <button type='button' aria-pressed={mediaTab === 'images'} onClick={() => setMediaTab('images')}>
                    Images & logos
                  </button>
                  <button
                    type='button'
                    aria-pressed={mediaTab === 'documents'}
                    onClick={() => setMediaTab('documents')}
                  >
                    Documents PDF
                  </button>
                </div>
                <div hidden={mediaTab !== 'images'}>
                  <SiteCmsImages draft={current} onChange={edit} onMetadataChanged={metaChanged} />
                </div>
                <div hidden={mediaTab !== 'documents'}>
                  <SiteCmsDocuments
                    draft={current}
                    documents={documents.data ?? []}
                    loading={documents.isPending}
                    failed={documents.isError}
                    retry={() => void documents.refetch()}
                    onMetadataChanged={metaChanged}
                  />
                </div>
              </div>
            )}
            {visited.includes('publication') && (
              <div hidden={section !== 'publication'}>
                <SiteCmsPublicationPanel
                  active={section === 'publication'}
                  draft={current}
                  unsavedChanges={dirty || save.isPending || Boolean(accessSuspended)}
                  pendingForms={pendingCount > 0}
                  libraryBusy={libraryBusy > 0}
                  accessSuspended={Boolean(accessSuspended)}
                  editorReloading={reloading}
                  onSave={submit}
                  onNavigate={navigate}
                  onOpenIssue={openIssue}
                />
              </div>
            )}
            {section === 'settings' && (
              <section className='cms-card'>
                <h3>Identité & coordonnées</h3>
                <div className='cms-form-grid'>
                  <label>
                    Nom du site
                    <input
                      value={current.title}
                      maxLength={120}
                      onChange={e => edit({ ...current, title: e.target.value })}
                    />
                  </label>
                  <label>
                    Description générale
                    <textarea
                      rows={3}
                      maxLength={320}
                      value={current.description}
                      onChange={e => edit({ ...current, description: e.target.value })}
                    />
                  </label>
                  {(['contactEmail', 'contactPhone', 'contactAddress', 'footerText'] as const).map(field => (
                    <label key={field}>
                      {
                        {
                          contactEmail: 'Email public',
                          contactPhone: 'Téléphone public',
                          contactAddress: 'Adresse publique',
                          footerText: 'Texte du pied de page',
                        }[field]
                      }
                      <input
                        value={current.branding?.[field] ?? ''}
                        maxLength={field === 'contactEmail' ? 180 : field === 'contactPhone' ? 50 : 500}
                        onChange={e =>
                          edit({
                            ...current,
                            branding: {
                              ...defaultCmsBranding(current.theme),
                              ...current.branding,
                              [field]: e.target.value,
                            },
                          })
                        }
                      />
                    </label>
                  ))}
                </div>
                <p className='cms-hint'>Ne renseignez que les coordonnées destinées au public.</p>
              </section>
            )}
          </fieldset>
          <footer className='cms-workspace-footer'>
            <span>Révision {current.revision} · Brouillon privé</span>
            <button
              type='button'
              className='cms-link-button'
              disabled={reloading || save.isPending || operationCount > 0 || libraryBusy > 0 || accessSuspended}
              onClick={() => void reloadDraft()}
            >
              <RefreshCw size={14} />
              Recharger
            </button>
          </footer>
        </section>
      </div>
      {preview && (
        <SiteCmsDialog wide title='Aperçu privé du brouillon' onClose={() => setPreview(false)}>
          <p className='cms-hint'>
            Vos saisies non enregistrées sont incluses. Pour voir ce qui sera diffusé, ouvrez Publication.
          </p>
          <div className='cms-row'>
            <label>
              Page à prévisualiser
              <select value={previewPageSlug} onChange={e => setPreviewPageSlug(e.target.value)}>
                {current.pages.map(p => (
                  <option key={p.slug} value={p.slug}>
                    {p.title}
                    {p.visibility === 'DRAFT' ? ' — privée' : ''}
                  </option>
                ))}
              </select>
            </label>
            <div className='cms-segmented'>
              <button
                type='button'
                aria-label='Aperçu ordinateur'
                aria-pressed={!previewMobile}
                onClick={() => setPreviewMobile(false)}
              >
                <Monitor size={18} />
              </button>
              <button
                type='button'
                aria-label='Aperçu mobile'
                aria-pressed={previewMobile}
                onClick={() => setPreviewMobile(true)}
              >
                <Smartphone size={18} />
              </button>
            </div>
          </div>
          <SiteCmsPreview
            draft={current}
            pageSlug={previewPageSlug}
            mobile={previewMobile}
            onNavigatePage={setPreviewPageSlug}
          />
        </SiteCmsDialog>
      )}
    </div>
  );
}
